import SaveAltIcon from "@material-ui/icons/SaveAlt"
import AppLayout from "@/components/AppLayout"
import {FormEvent, useState} from "react"
import {useRouter} from "next/router"
import PptInputTailwind from "@/components/PptInputTailwind"
import {trpc} from "@/lib/requests/index"
import {MIN_PASSWORD_LENGTH} from "@/lib/consts"

export default function Password(): JSX.Element {
  const router = useRouter()
  
  const [oldPass, setOldPass] = useState("")
  const [newPass, setNewPass] = useState("")
  const [confirmPass, setConfirmPass] = useState("")
  const [fieldErrors, setFieldErrors] = useState<{
    oldPass?: string
    newPass?: string
    confirmPass?: string
  }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  function validateField(field: 'oldPass' | 'newPass' | 'confirmPass', value: string) {
    const errors = { ...fieldErrors }
    
    switch (field) {
      case 'oldPass':
        if (!value.trim()) {
          errors.oldPass = "Old password is required"
        } else {
          delete errors.oldPass
        }
        break
        
      case 'newPass':
        if (!value.trim()) {
          errors.newPass = "New password is required"
        } else if (value.length < MIN_PASSWORD_LENGTH) {
          errors.newPass = `New password must be at least ${MIN_PASSWORD_LENGTH} characters long`
        } else if (oldPass && value === oldPass) {
          errors.newPass = "New password must be different from the old password"
        } else {
          delete errors.newPass
        }
        
        // Also revalidate confirm password when new password changes
        if (confirmPass && value !== confirmPass) {
          errors.confirmPass = "New password and confirmation password do not match"
        } else if (confirmPass && value === confirmPass) {
          delete errors.confirmPass
        }
        break
        
      case 'confirmPass':
        if (!value.trim()) {
          errors.confirmPass = "Password confirmation is required"
        } else if (newPass && value !== newPass) {
          errors.confirmPass = "New password and confirmation password do not match"
        } else {
          delete errors.confirmPass
        }
        break
    }
    
    setFieldErrors(errors)
  }

  function validateAllFields(): boolean {
    const errors: { oldPass?: string; newPass?: string; confirmPass?: string } = {}
    
    // Check if all fields are filled
    if (!oldPass.trim()) {
      errors.oldPass = "Old password is required"
    }
    if (!newPass.trim()) {
      errors.newPass = "New password is required"
    }
    if (!confirmPass.trim()) {
      errors.confirmPass = "Password confirmation is required"
    }
    
    // Check password length
    if (newPass.trim() && newPass.length < MIN_PASSWORD_LENGTH) {
      errors.newPass = `New password must be at least ${MIN_PASSWORD_LENGTH} characters long`
    }
    
    // Check password confirmation match
    if (newPass && confirmPass && newPass !== confirmPass) {
      errors.confirmPass = "New password and confirmation password do not match"
    }
    
    // Check that new password is different from old password
    if (oldPass && newPass && oldPass === newPass) {
      errors.newPass = "New password must be different from the old password"
    }
    
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const updatePasswordMut = trpc.useMutation([
    "user.updatePassword"
  ])

  async function updatePassword(
    oldPassword: string, 
    newPassword: string
  ) {
    const data = {oldPassword, newPassword}
    console.table(data)
    const ok = await updatePasswordMut.mutateAsync(
      data
    )
    console.log('password change ok?', ok)
    if (ok) {
      router.push("/account")
    } else {
      window.alert("An error occurred when changing password. Please contact support!")
    }
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const isValid = validateAllFields()
    if (!isValid) {
      return
    }
    setIsSubmitting(true)
    updatePassword(oldPass, newPass)
  }

  return <AppLayout requiredAuth="user">
    <form 
      onSubmit={handleSubmit} 
      className=""
    >
      <section className="app-layout-content mx-auto">
        <div className="flex flex-col items-center my-12">
          <h1 className="text-3xl sm:text-4xl font-display">
            {"Modify Password"}
          </h1>
        </div>
        
        <div className="box-content w-full p-4 grid grid-cols-6 gap-4 items-start">
          <div className="col-span-6 w-full">
            <PptInputTailwind
              className="w-full"
              labelText="Old Password"
              inputClassName="w-full"
              type="password"
              value={oldPass}
              onChange={(e) => {
                const value = e.target.value
                setOldPass(value)
                validateField('oldPass', value)
              }}
            />
            {fieldErrors.oldPass && (
              <div className="text-sm text-red-600 mt-1">
                {fieldErrors.oldPass}
              </div>
            )}
          </div>
          
          <div className="col-span-6 w-full">
            <PptInputTailwind
              className="w-full"
              labelText="New Password"
              inputClassName="w-full"
              type="password"
              value={newPass}
              onChange={(e) => {
                const value = e.target.value
                setNewPass(value)
                validateField('newPass', value)
              }}
            />
            
            {fieldErrors.newPass && (
              <div className="text-sm text-red-600 mt-1">
                {fieldErrors.newPass}
              </div>
            )}
          </div>
          
          <div className="col-span-6 w-full">
            <PptInputTailwind
              className="w-full"
              labelText="Confirm Password"
              inputClassName="w-full"
              type="password"
              value={confirmPass}
              onChange={(e) => {
                const value = e.target.value
                setConfirmPass(value)
                validateField('confirmPass', value)
              }}
            />
            {fieldErrors.confirmPass && (
              <div className="text-sm text-red-600 mt-1">
                {fieldErrors.confirmPass}
              </div>
            )}
          </div>
        </div>
        <button
          type="submit"
          disabled={isSubmitting}
          className={
            "flex items-center font-display text-3xl sm:text-4xl mt-4" +
            (isSubmitting 
              ? " text-gray-400 cursor-not-allowed" 
              : " text-ppt-blue hover:text-ppt-success border-b-4 border-transparent hover:border-ppt-success"
            )
          }
        >
          <SaveAltIcon
            style={{
              width: "2rem",
              height: "2rem",
              marginRight: "0.25rem",
            }}
          />
          {isSubmitting ? "saving..." : "save"}
        </button>
      </section>
    </form>
  </AppLayout>
}
